import Vue from 'vue';
import { HTTP } from '@/utils/httpCommonUtil';
import VueSimpleSpinner from "vue-simple-spinner";
import HttpMixin from '@/mixins/HttpMixin';
import {UserRoleEnum} from '@/enums/userRolesEnum';
import InvoiceEventHub from '@/components/invoice/InvoiceEventHub';

export default Vue.component('invoice-providers-payable', {
  name: 'InvoiceProvidersPayable',
  props: {
    'facilityId': { type: String },
    'specialtyId': { type: String },
    'invoiceDate': { type: String },
    'invoiceId': { type: [String, Number] },
    'mergedInvoiceId': { type: [String, Number] },
    'isSpecial': {type: Boolean }
  },
  components: {
    VueSimpleSpinner
  },
  mixins: [ HttpMixin ],
  data () {
    return {
      loadingInitialData: false,
      cardExpanded: false,
      transactionData: null,
      facilityData: [],
      payments: []
    };
  },
  computed: {
    isPotential (): boolean {
      return !(Number(this.invoiceId) > 0);
    },
    canRemoveAtLeastOne (): boolean {
      if (!this.transactionData || this.isPotential) {
        return false;
      }
      return (this.transactionData as any).providers.some((provider: any) => provider.isInQueue);
    },
    canAddAtLeastOne (): boolean {
      if (!this.transactionData || this.isPotential) {
        return false;
      }
      return (this.transactionData as any).providers.some((provider: any) => !provider.isInQueue && (provider.payableTotal - provider.amountPaid > 0));
    },
    isFacilityUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT);
    },
  },
  async created () {
    await this.expandCard();
    InvoiceEventHub.$on('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  beforeDestroy: function () {
    InvoiceEventHub.$off('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  methods: {
    // this comment copypasta is to denote that this function is to work around a leak caused by using anonymous functions in the event callback.
    // per this issue comment https://github.com/vuejs/vue-router/issues/1181#issuecomment-281084486
    // testing bears this out to be true and was causing unnecessary data fetches as well as bugs with incorrect route redirects
    dataFetchWrapper() {
      this.getTransactionInfo().then(() => {}).catch(() => '');
    },
    async expandCard (): Promise<void> {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        await this.getTransactionInfo();
      }
    },
    async getTransactionInfo () {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        const url = this.isSpecial ? this.getSpecialInvoiceUrl() : this.getInvoiceUrl();
        const [response, payments] = await Promise.all([
          HTTP.get(url) as any,
          this.getPayments()
        ]);
        clearTimeout(showLoadingSpinnerTimeout);
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Invoice Information.");
        }
        this.payments = payments;
        const transactionData = response.data.data;
        transactionData.providers.forEach((provider: any) => {
          provider.isEditing = false;
          provider.showPayments = false;
          if (provider.shifts) {
            provider.shifts.forEach((shift: any) => shift.isEditing);
          }
        });
        this.transactionData = transactionData;
        this.setFacilityUserData();
        this.loadingInitialData = false;
      } catch (err) {
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Invoice Information.'
        });
      }
    },
    async getPayments() {
      if (!this.invoiceId || this.isFacilityUser) {
        return [];
      }
      const response = await HTTP.get(`/api/provider-payments/invoices/${this.invoiceId}`);
      if (!response.data || !response.data.data) {
        throw new Error("Error retrieving Provider Payments for Invoice.");
      }
      return response.data.data;
    },
    getInvoiceUrl() {
      let parameters;
      if (this.mergedInvoiceId) {
        parameters = `mergedInvoiceId=${this.mergedInvoiceId}`;
      } else if (this.invoiceId) {
        parameters = `invoiceId=${this.invoiceId}`;
      } else if (this.facilityId && this.specialtyId && this.invoiceDate) {
        parameters = `facilityId=${this.facilityId}&specialtyId=${this.specialtyId}&invoiceDate=${this.invoiceDate}`;
      } else {
        throw new Error("Error retrieving Invoice Information");
      }
      return `/api/invoices/transactions/payable?${parameters}`;
    },
    getSpecialInvoiceUrl() {
      return `/api/special-invoices/transactions/payable?invoiceId=${this.invoiceId}`;
    },
    setFacilityUserData() {
      if (!this.isFacilityUser || !this.transactionData) {
        return;
      }
      (this.transactionData as any).providers.forEach((providerData: any) => {
        const data = new Map<number, {id: number, name: string, label: string, quantity: number, rate: number}>();
        for (const shift of providerData.shifts) {
          for (const transaction of shift.transactions) {
            let tran = data.get(transaction.transactionTypeId);
            if (!tran) {
              tran = {
                id: transaction.transactionTypeId,
                name: transaction.transactionTypeName,
                label: transaction.transactionTypeQuantityLabel,
                quantity: 0,
                rate: transaction.billRateCalculated
              };
              data.set(transaction.transactionTypeId, tran);
            }
            tran.quantity += transaction.quantity;
          }
        }
        providerData.facilityData = Array.from(data.values());
        providerData.facilityData.sort((a: any, b: any) => a.name.localeCompare(b.name));
      });
    },
    toggleShift (shift: any) {
      shift.isEditing = !shift.isEditing;
      // **HACK** to make Vue aware of the changes (see https://vuejs.org/v2/guide/list.html#Array-Change-Detection)
      // This makes the Shift's Transactions expand... without this you click the shift and nothing occurs
      for (const provider of (this.transactionData as any).providers) {
        if (provider.providerId === shift.providerId) {
          provider.shifts = provider.shifts.map((shift: any) => shift);
          break;
        }
      }
    },
    toggleProvider (provider: any) {
      if (!this.isSpecial) {
        provider.isEditing = !provider.isEditing;
      }
    },
    async addToQueue (providerId = 0) {
      const body = {invoiceId: this.invoiceId, providerId: providerId};
      const data = await (this as any).httpPost('/provider-payments/queue', 'An error occurred deleting Providers from Cart.', body, false);
      if (!data) {
        return;
      }
      this.$notify({
        group: 'main',
        type: 'success',
        title: 'Success',
        text: 'Inserted ' + data[0] + ' Providers to cart.'
      });
      await this.getTransactionInfo();
    },
    async removeFromQueue (providerId = 0) {
      let url = '/provider-payments/queue?invoiceId=' + this.invoiceId;
      if (Number(providerId) > 0) {
        url += '&providerId=' + providerId;
      }
      const message = await (this as any).httpDelete(url, 'An error occurred deleting Providers from Cart.', false);
      if (!message) {
        return;
      }
      this.$notify({
        group: 'main',
        type: 'success',
        title: 'Success',
        text: message
      });
      await this.getTransactionInfo();
    },
    browseToShift (shiftId: number) {
      window.open("/shifts/" + shiftId, '_blank');
    },
    browseToProvider (providerId: number) {
      window.open("/providers/" + providerId, '_blank');
    },
    getPaymentsForProvider(providerId: number) {
      return this.payments.filter((payment: any) => payment.providerId === providerId);
    },
    paymentText(providerId: number) {
      const paymentCount = this.getPaymentsForProvider(providerId).length;
      let text = `${paymentCount} payment`;
      if (paymentCount > 1) {
        text += 's';
      }
      return text;
    },
    showPaymentTextForProvider(provider: any) {
      return provider.showPayments ? 'Hide Payments' : 'Show Payments';
    },
    togglePayments(provider: any) {
      provider.showPayments = !provider.showPayments;
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/invoice/cards/payable/InvoiceProviderPayable.ts